import { useEffect, useRef, useState } from 'react';
import { MoreVertical, Search } from 'lucide-react';
import { t } from '../lib/i18n';

// Back-office building blocks (s134 redesign). Every admin page composes these
// so the 9 surfaces read as one system: page header, stat cards, status pills
// with counts, a grid table, status badges, empty panels and a ⋮ row menu.

export const adminBtnPrimary =
  'lms-press inline-flex h-11 items-center justify-center gap-2 rounded-[10px] bg-lms-accent px-5 text-sm font-bold text-lms-accent-ink hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50';
export const adminBtnOutline =
  'lms-press inline-flex h-11 items-center justify-center gap-2 rounded-[10px] border border-lms-ink bg-lms-surface px-4 text-sm font-bold text-lms-ink hover:bg-lms-raised disabled:opacity-50';
export const adminBtnDanger =
  'lms-press inline-flex h-11 items-center justify-center gap-2 rounded-[10px] border border-lms-sale bg-lms-surface px-4 text-sm font-bold text-lms-sale hover:bg-lms-raised disabled:opacity-50';
export const adminInput =
  'h-11 w-full rounded-[10px] border border-lms-line bg-lms-input px-3.5 text-sm text-lms-ink outline-none focus:border-[var(--lms-focus-border)]';
export const adminCard = 'rounded-xl border border-lms-line bg-lms-surface';

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="flex flex-col gap-1">
        <h1 className="text-[28px] font-bold leading-tight text-lms-ink">{title}</h1>
        {subtitle && <p className="text-sm text-lms-sub">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2.5">{children}</div>}
    </div>
  );
}

export function StatCards({ items }) {
  return (
    <div className={`grid grid-cols-2 gap-4 ${items.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
      {items.map(({ label, value, note, accent }) => (
        <div
          key={label}
          className={`flex flex-col gap-1 rounded-xl bg-lms-surface px-5 py-[18px] ${accent ? 'border-2 border-lms-accent' : 'border border-lms-line'}`}
        >
          <span className="text-[13px] text-lms-sub">{label}</span>
          <span className={`text-[26px] font-bold leading-tight lms-num ${accent ? 'text-lms-free' : 'text-lms-ink'}`}>{value}</span>
          {note && <span className="text-xs text-lms-faint">{note}</span>}
        </div>
      ))}
    </div>
  );
}

// Pills = segmented filter with counts. items: [{ key, label, count? }]
export function FilterPills({ items, value, onChange, children }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div role="tablist" className="flex flex-wrap gap-2.5">
        {items.map((it) => {
          const on = it.key === value;
          return (
            <button
              key={it.key}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => onChange(it.key)}
              className={`lms-press inline-flex h-10 items-center gap-2 rounded-full border px-4 text-sm font-medium ${
                on ? 'border-lms-ink bg-lms-ink text-lms-surface' : 'border-lms-pill bg-lms-surface text-lms-ink hover:border-lms-ink'
              }`}
            >
              {it.label}
              {typeof it.count === 'number' && (
                <span className={`text-xs lms-num ${on ? 'opacity-75' : 'text-lms-sub'}`}>{it.count}</span>
              )}
            </button>
          );
        })}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2.5">{children}</div>}
    </div>
  );
}

export function SearchBox({ value, onChange, placeholder, className = 'w-72' }) {
  return (
    <label className={`flex h-10 items-center gap-2 rounded-[10px] border border-lms-line bg-lms-surface px-3.5 text-lms-faint focus-within:border-[var(--lms-focus-border)] ${className}`}>
      <Search size={15} />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="w-full bg-transparent text-sm text-lms-ink outline-none"
      />
    </label>
  );
}

export function SelectBox({ value, onChange, children, label, className = '' }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      className={`h-10 rounded-[10px] border border-lms-line bg-lms-surface px-3 text-sm font-medium text-lms-ink outline-none focus:border-[var(--lms-focus-border)] ${className}`}
    >
      {children}
    </select>
  );
}

// Grid table. cols: [{ label, width, align }]; rows rendered by the caller as
// <TableRow cols={cols}>{cells}</TableRow>.
export function DataTable({ cols, children, empty }) {
  const tracks = cols.map((c) => c.width || 'minmax(0,1fr)').join(' ');
  return (
    <div className={`${adminCard} overflow-x-auto`}>
      <div className="min-w-[760px]">
        <div
          className="grid gap-4 border-b border-lms-line bg-lms-raised px-5 py-3 text-xs font-semibold uppercase tracking-[0.04em] text-lms-sub"
          style={{ gridTemplateColumns: tracks }}
        >
          {cols.map((c) => <span key={c.label} className={c.align === 'right' ? 'text-right' : ''}>{t(c.label)}</span>)}
        </div>
        {empty || children}
      </div>
    </div>
  );
}

export function TableRow({ cols, children, onClick }) {
  const tracks = cols.map((c) => c.width || 'minmax(0,1fr)').join(' ');
  return (
    <div
      className={`grid items-center gap-4 border-b border-lms-line px-5 py-3.5 last:border-b-0 ${onClick ? 'cursor-pointer hover:bg-lms-raised' : ''}`}
      style={{ gridTemplateColumns: tracks }}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

export function EmptyPanel({ icon: Icon, title, children, bare = false }) {
  return (
    <div className={`flex flex-col items-center gap-2.5 px-6 py-11 text-center ${bare ? '' : adminCard}`}>
      {Icon && (
        <span className="flex h-[52px] w-[52px] items-center justify-center rounded-[14px] bg-lms-tag-free-bg text-lms-tag-free-ink">
          <Icon size={24} />
        </span>
      )}
      <span className="text-base font-bold text-lms-ink">{title}</span>
      {children && <div className="max-w-[500px] text-sm leading-relaxed text-lms-sub">{children}</div>}
    </div>
  );
}

const BADGE = {
  ok: 'bg-lms-tag-free-bg text-lms-tag-free-ink',
  warn: 'bg-lms-tag-alt-bg text-lms-tag-alt-ink',
  info: 'bg-lms-current text-lms-ink',
  bad: 'bg-[color-mix(in_srgb,var(--lms-sale)_16%,transparent)] text-lms-sale',
  neutral: 'bg-lms-raised text-lms-body',
};
export function Badge({ kind = 'neutral', children }) {
  return (
    <span className={`inline-flex justify-self-start rounded-full px-2.5 py-[3px] text-xs font-bold ${BADGE[kind] || BADGE.neutral}`}>
      {children}
    </span>
  );
}

export function statusBadge(status) {
  const map = {
    active: ['ok', 'Active'], pending: ['warn', 'Pending review'], draft: ['neutral', 'Draft'],
    approved: ['ok', 'Approved'], rejected: ['bad', 'Rejected'], paid: ['ok', 'Paid'],
  };
  const [kind, label] = map[status] || ['neutral', status || '—'];
  return <Badge kind={kind}>{t(label)}</Badge>;
}

export function Avatar({ name, size = 36 }) {
  const initials = (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-full bg-lms-tag-free-bg font-bold text-lms-tag-free-ink"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
    >
      {initials}
    </span>
  );
}

export function PersonCell({ name, email }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <Avatar name={name} />
      <div className="min-w-0">
        <div className="truncate text-[14.5px] font-bold text-lms-ink">{name || '—'}</div>
        {email && <div className="truncate text-[12.5px] text-lms-sub">{email}</div>}
      </div>
    </div>
  );
}

export function Thumb({ src, title, w = 56, h = 32 }) {
  const [broken, setBroken] = useState(false);
  if (src && !broken) return <img src={src} alt="" onError={() => setBroken(true)} className="shrink-0 rounded-md object-cover" style={{ width: w, height: h }} />;
  const initials = (title || '?').split(/\s+/).filter((x) => /\w/.test(x)).slice(0, 2).map((x) => x[0]).join('').toUpperCase();
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-md bg-lms-current text-[10.5px] font-bold text-lms-sidebar"
      style={{ width: w, height: h }}
    >
      {initials}
    </span>
  );
}

export function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// ⋮ menu: items = [{ label, icon, onClick, danger }]
export function RowMenu({ items, label }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div ref={ref} className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        aria-label={label || t('More actions')}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="lms-press flex h-9 w-9 items-center justify-center rounded-lg text-lms-sub hover:bg-lms-raised hover:text-lms-ink"
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <div className="absolute right-0 top-10 z-20 w-52 overflow-hidden rounded-lg border border-lms-line bg-lms-surface py-1 shadow-[var(--lms-elev-3)]">
          {items.filter(Boolean).map(({ label: l, icon: Icon, onClick, danger }) => (
            <button
              key={l}
              type="button"
              onClick={() => { setOpen(false); onClick(); }}
              className={`flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm hover:bg-lms-raised ${danger ? 'text-lms-sale' : 'text-lms-ink'}`}
            >
              {Icon && <Icon size={15} />} {l}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function IconButton({ label, onClick, children }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className="lms-press flex h-9 w-9 items-center justify-center rounded-lg border border-lms-line bg-lms-surface text-lms-ink hover:border-lms-ink"
    >
      {children}
    </button>
  );
}

export function Switch({ checked, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-[42px] shrink-0 rounded-full transition-colors ${checked ? 'bg-lms-accent' : 'bg-lms-faint'}`}
    >
      <span className={`absolute left-0 top-0.5 h-5 w-5 rounded-full bg-lms-chip-bg shadow-[var(--lms-shadow-sm)] transition-transform ${checked ? 'translate-x-[20px]' : 'translate-x-0.5'}`} />
    </button>
  );
}
